import { useState, useEffect, useMemo } from "react";
import { Box, Tabs, Tab, Paper } from "@mui/material";

import VipConfig from "./VipConfig";
import DiamondConfig from "./DiamondConfig";
import MetalRewards from "../MetalRewards/MetalRewards";
import ReferralBenefits from "../Referral/ReferralBenefits";
import AccessControl from "../../Comman/AccessControl";

import { controlAllFeatureOptions } from "../../../configs/constants";
import { canShowPermission } from "../Permissions/permissionObject";
import InfoButton from "../../Comman/InfoButton";

export default function Offers() {
  const [selectedTabLabel, setSelectedTabLabel] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [formData, setFormData] = useState({
    flashOffersInput: "",
  });

  /* -------------------- Load Permissions -------------------- */
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      setPermissions(JSON.parse(storedPermissions));
    }
  }, []);

  /* -------------------- Permission Flags -------------------- */
  const canViewDiamondPoints = canShowPermission(permissions, "diamondPoints", "view");
  const canViewVIPcustomers = canShowPermission(permissions, "vipConfig", "view");
  const canViewMetalRewards = canShowPermission(permissions, "metalRewards", "view");
  const canViewReferral = canShowPermission(permissions, "referralBenefits", "view");

  const visibleTabLabelsKey = useMemo(
    () =>
      [
        canViewDiamondPoints && "Diamond Points",
        canViewVIPcustomers && "VIP Customers",
        canViewMetalRewards && "Metal Rewards",
        canViewReferral && "Referral",
      ]
        .filter(Boolean)
        .join("|"),
    [
      canViewDiamondPoints,
      canViewVIPcustomers,
      canViewMetalRewards,
      canViewReferral,
    ]
  );

  /* -------------------- Tabs Config (Single Source of Truth) -------------------- */
  const tabs = useMemo(
    () =>
      [
        canViewDiamondPoints && {
          label: "Diamond Points",
          feature: controlAllFeatureOptions.Diamond_Points,
          component: <DiamondConfig />,
        },
        canViewVIPcustomers && {
          label: "VIP Customers",
          feature: controlAllFeatureOptions.VIP_Customer,
          component: <VipConfig />,
        },

        canViewMetalRewards && {
          label: "Metal Rewards",
          feature: controlAllFeatureOptions.Metal_Rewards,
          component: <MetalRewards />,
        },
        canViewReferral && {
          label: "Referral",
          feature: controlAllFeatureOptions.Referral_Benefit,
          component: <ReferralBenefits />,
        },
      ].filter(Boolean),
    [permissions, formData.flashOffersInput]
  );

  /* Keep selection valid when visible tabs change (permissions / list length) */
  useEffect(() => {
    const labels = visibleTabLabelsKey.split("|").filter(Boolean);
    if (!labels.length) return;
    const stillVisible =
      selectedTabLabel && labels.includes(selectedTabLabel);
    if (!stillVisible) {
      setSelectedTabLabel(labels[0]);
    }
  }, [visibleTabLabelsKey, selectedTabLabel]);

  const activeTab =
    tabs.find((t) => t.label === selectedTabLabel) ?? tabs[0] ?? null;
  const tabsValue = activeTab?.label ?? false;

  /* -------------------- Render -------------------- */
  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          width: "100%",
          mt: { xs: 10, md: 10 },
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 2, borderRadius: "16px" }} elevation={1}>
          {/* -------------------- Tabs Header -------------------- */}
          <Tabs
            value={tabsValue}
            onChange={(e, newLabel) => setSelectedTabLabel(newLabel)}
            variant="fullWidth"
            sx={{
              mb: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "4px",
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                borderRadius: "6px",
                margin: "0 4px",
                minHeight: "48px",
                "&.Mui-selected": {
                  backgroundColor: "#fff",
                  color: "#7A3FFF",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  borderBottom: "3px solid #7A3FFF",
                },
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.label} value={tab.label} label={tab.label} />
            ))}
          </Tabs>

          {/* -------------------- Tab Content -------------------- */}
          <Box sx={{ mt: 0 }}>
            {activeTab?.feature ? (
              <AccessControl feature={activeTab.feature}>
                {activeTab.component}
              </AccessControl>
            ) : (
              activeTab?.component
            )}
          </Box>
        </Paper>
      </Box>
      <Box
    sx={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 1000,
    }}
  >
    <InfoButton pageName="OFFER" />
  </Box>
    </Box>
    </Box>
  );
}
